/**
 * keyState — tracks which physical keys are down and for how long.
 *
 * This is a thin DOM adapter: it normalizes keyboard events into a stable,
 * layout-independent key identity, suppresses OS auto-repeat, records a
 * press timestamp per key, and emits `down` / `up` (with duration) callbacks.
 *
 * It knows nothing about effects or the paramBus. It just answers "what's held,
 * and for how long" and fires events. The mapping to effects lives one layer up
 * in InputController, so this file never needs to change when the key config does.
 */

export interface KeyStateEvents {
  onDown?: (key: string, event: KeyboardEvent) => void;
  onUp?: (key: string, durationMs: number, event: KeyboardEvent) => void;
}

/**
 * Normalize a KeyboardEvent to a physical-key identity independent of layout,
 * Shift state, and locale:
 *   KeyB   -> 'B'      Digit3 -> '3'      Numpad5 -> '5'
 *   Space  -> 'Space'  ShiftLeft -> 'ShiftLeft'  (passed through)
 * Keying on `code` (not `key`) means 'B' is 'B' whether or not Shift is held,
 * so a future Shift-amplify modifier reads `event.shiftKey` without changing identity.
 */
export function normalizeKey(event: KeyboardEvent): string {
  const c = event.code;
  if (c.startsWith('Key')) return c.slice(3);
  if (c.startsWith('Digit')) return c.slice(5);
  if (c.startsWith('Numpad')) return c.slice(6);
  return c;
}

export class KeyState {
  /** key -> press timestamp (performance.now() ms). Presence == currently down. */
  private readonly pressedAt = new Map<string, number>();
  private events: KeyStateEvents;
  private detachers: Array<() => void> = [];
  private readonly now: () => number;

  constructor(events: KeyStateEvents = {}, now: () => number = defaultNow) {
    this.events = events;
    this.now = now;
  }

  isDown(key: string): boolean {
    return this.pressedAt.has(key);
  }

  heldKeys(): string[] {
    return [...this.pressedAt.keys()];
  }

  /** How long `key` has been held (ms), or 0 if it isn't down. */
  durationOf(key: string): number {
    const t = this.pressedAt.get(key);
    return t === undefined ? 0 : this.now() - t;
  }

  /** Feed a keydown. Ignores OS auto-repeat and already-held keys. */
  press(key: string, event: KeyboardEvent): void {
    if (this.pressedAt.has(key)) return;
    this.pressedAt.set(key, this.now());
    this.events.onDown?.(key, event);
  }

  /** Feed a keyup. No-op if the key wasn't tracked as down. */
  release(key: string, event: KeyboardEvent): void {
    const t = this.pressedAt.get(key);
    if (t === undefined) return;
    this.pressedAt.delete(key);
    this.events.onUp?.(key, this.now() - t, event);
  }

  /** Release everything (e.g. on window blur) so no key gets stuck "down". */
  releaseAll(event: KeyboardEvent): void {
    for (const key of [...this.pressedAt.keys()]) {
      this.release(key, event);
    }
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return; // OS auto-repeat — one logical press only
    this.press(normalizeKey(event), event);
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.release(normalizeKey(event), event);
  };

  private handleBlur = (): void => {
    // Synthesize a bare event so `up` handlers still get a shape they expect.
    this.releaseAll(new KeyboardEvent('keyup'));
  };

  /** Bind to the DOM. Returns a detach function. */
  attach(target: Window | HTMLElement = window): () => void {
    const kd = this.handleKeyDown as EventListener;
    const ku = this.handleKeyUp as EventListener;
    target.addEventListener('keydown', kd);
    target.addEventListener('keyup', ku);
    window.addEventListener('blur', this.handleBlur);
    const detach = () => {
      target.removeEventListener('keydown', kd);
      target.removeEventListener('keyup', ku);
      window.removeEventListener('blur', this.handleBlur);
    };
    this.detachers.push(detach);
    return detach;
  }

  detach(): void {
    for (const d of this.detachers.splice(0)) d();
  }
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
